import { useMemo } from 'react';
import { Textarea, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { ChatOptionsProps } from '../ChatOptions.types';
import {
  getDefaultTranslationOptions,
  SUPPORTED_RESPONSE_STYLE,
  SUPPORTED_LANGUAGE_OPTIONS,
  transformTranslationOptionsV2,
  TranslationOptions,
  ResponseStyle,
} from './TranslationAgentOptions.types';
import { DEFAULT_SELECTABLE_MODEL, useAgentModelOptions } from '../hooks/useAgentModelOptions';
import { useOptionsManager } from '../hooks/useOptionsManager';
import { LanguageMultiSelect } from './LanguageMultiSelect';
import { useTranslation } from 'react-i18next';

export const TranslationAgentOptions = ({ onOptionUpdated }: ChatOptionsProps) => {
  const { t } = useTranslation('chats');

  const filteredModels = useAgentModelOptions();
  const hasModels = filteredModels.length > 0;

  const defaultOptions = useMemo(() => {
    const firstModel = filteredModels[0]?.value ?? DEFAULT_SELECTABLE_MODEL;
    return getDefaultTranslationOptions(firstModel);
  }, [filteredModels]);

  const { options, setOptions } = useOptionsManager<TranslationOptions>({
    defaultOptions,
    onOptionUpdated,
    toStore: transformTranslationOptionsV2,
  });

  return (
    <div className="flex flex-col gap-4 w-80">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('chats.translationAgentOptions.translateAgentSettings')}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.translationAgentOptions.language')}
        </span>
        <LanguageMultiSelect
          value={options.targetLanguages}
          onChange={(value) => setOptions({ targetLanguages: value })}
          options={SUPPORTED_LANGUAGE_OPTIONS}
          disabled={!hasModels}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.translationAgentOptions.demographic')}
        </span>
        <Textarea
          placeholder={t('chats.translationAgentOptions.describeTargetDemographic')}
          value={options.targetDemographic ?? ''}
          onChange={(event) => setOptions({ targetDemographic: event.target.value })}
          disabled={!hasModels}
          className="border-0 bg-surface min-h-24 resize-none"
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.translationAgentOptions.toneOfVoice')}
        </span>
        <Textarea
          placeholder={t('chats.translationAgentOptions.describeToneOfVoice')}
          value={options.toneOfVoice ?? ''}
          onChange={(event) => setOptions({ toneOfVoice: event.target.value })}
          disabled={!hasModels}
          className="border-0 bg-surface min-h-24 resize-none"
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.translationAgentOptions.responseStyle')}
        </span>
        <ToggleGroup
          type="single"
          value={options.responseStyle}
          onValueChange={(value) => {
            if (value) setOptions({ responseStyle: value as ResponseStyle });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-3 w-full"
          disabled={!hasModels}
        >
          {SUPPORTED_RESPONSE_STYLE.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value} className="py-2">
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </div>
  );
};
